'use client'

import { cn } from '@/lib/utils'
import { Check, Circle, Star, Archive } from 'lucide-react'

export type MemberStatus = 'active' | 'inactive' | 'deceased' | 'archived'

interface StatusBadgeProps {
    status: MemberStatus
    className?: string
    showLabel?: boolean
}

const statusConfig: Record<MemberStatus, {
    label: string
    icon: typeof Check
    bgColor: string
    textColor: string
    borderColor: string
    ariaLabel: string
}> = {
    active: {
        label: 'Active',
        icon: Check,
        // sage (reskin 2026-07-31): positive-secondary family; text stays
        // at 700 — never sage-500 as text (sub-AA)
        bgColor: 'bg-[var(--sage-50)]',
        textColor: 'text-[var(--sage-700)]',
        borderColor: 'border-[var(--sage-200)]',
        ariaLabel: 'Active member status',
    },
    inactive: {
        label: 'Inactive',
        icon: Circle,
        bgColor: 'bg-[var(--bg-subtle)]',
        textColor: 'text-[var(--text-secondary)]',
        borderColor: 'border-[var(--border-default)]',
        ariaLabel: 'Inactive member status',
    },
    deceased: {
        label: 'Deceased',
        icon: Star,
        bgColor: 'bg-[var(--bg-accent-subtle)]',
        textColor: 'text-[var(--text-accent)]',
        borderColor: 'border-[var(--accent-200)]',
        ariaLabel: 'Deceased member status',
    },
    archived: {
        label: 'Archived',
        icon: Archive,
        bgColor: 'bg-[var(--warning-50)]',
        textColor: 'text-[var(--warning-700)]',
        borderColor: 'border-[var(--warning-200)]',
        ariaLabel: 'Archived member status',
    },
}

/**
 * Accessible status badge with icon + color + label
 * - Uses icons for colorblind accessibility
 * - Includes ARIA labels for screen readers
 * - Meets WCAG 2.2 contrast requirements
 */
export function StatusBadge({
    status,
    className,
    showLabel = true
}: StatusBadgeProps) {
    const config = statusConfig[status]
    const Icon = config.icon

    return (
        <span
            role="status"
            aria-label={config.ariaLabel}
            className={cn(
                'inline-flex items-center gap-1 px-2.5 py-1',
                'text-xs font-semibold rounded-full border',
                config.bgColor,
                config.textColor,
                config.borderColor,
                className
            )}
        >
            <Icon
                className="h-3 w-3 flex-shrink-0"
                aria-hidden="true"
                strokeWidth={2.5}
            />
            {showLabel && (
                <span>{config.label}</span>
            )}
        </span>
    )
}

/**
 * Icon-only variant for compact displays
 * Maintains 44px touch target via padding
 */
export function StatusIcon({
    status,
    className
}: Omit<StatusBadgeProps, 'showLabel'>) {
    const config = statusConfig[status]
    const Icon = config.icon

    return (
        <span
            role="status"
            aria-label={config.ariaLabel}
            title={config.label}
            className={cn(
                'inline-flex items-center justify-center',
                'w-[var(--touch-target-min)] h-[var(--touch-target-min)]',
                'rounded-full border-2',
                config.bgColor,
                config.textColor,
                config.borderColor,
                className
            )}
        >
            <Icon
                className="h-5 w-5"
                aria-hidden="true"
                strokeWidth={2.5}
            />
        </span>
    )
}
